import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { kanban, projects, security } from "../../lib/api";
import { useUI } from "../../lib/store";
import { cn } from "../../lib/cn";
import { hashId } from "../../lib/format";
import {
  Folder,
  Inbox,
  MoreHorizontal,
  Plus,
  Settings,
} from "../shared/Icon";
import { IconButton } from "../shared/IconButton";
import {
  ContextMenu,
  CtxMenuItem,
  CtxMenuSeparator,
  Menu,
  MenuItem,
  MenuSeparator,
} from "../shared/Menu";
import { TypedConfirmDialog } from "../shared/ConfirmDialog";
import { Modal } from "../shared/Modal";
import { Button } from "../shared/Button";
import { Skeleton } from "../shared/Skeleton";
import type { Project, Task } from "../../lib/types";

interface Props {
  collapsed?: boolean;
  showArchived?: boolean;
  onNew: () => void;
  onEdit: (project: Project) => void;
}

/**
 * 8 muted theme-aware hues for the monogram tile. They're built from
 * accent-soft mixed with a tertiary hue offset by index — close to neutral,
 * never loud.
 */
const TILE_BACKGROUNDS = [
  "rgb(var(--accent-rgb) / 0.14)",
  "rgb(var(--accent-rgb) / 0.08)",
  "var(--status-success-soft)",
  "var(--status-paused-soft)",
  "var(--status-review-soft)",
  "var(--status-running-soft)",
  "var(--status-queued-soft)",
  "var(--status-error-soft)",
];

export function ProjectList({ collapsed, showArchived, onNew, onEdit }: Props) {
  const qc = useQueryClient();
  const {
    data: list,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["projects", { includeInactive: !!showArchived }] as const,
    queryFn: () => projects.list(!!showArchived),
  });

  const { data: board } = useQuery({
    queryKey: ["kanban", "all"],
    queryFn: () => kanban.board(undefined),
    refetchInterval: 5_000,
  });

  const { data: securitySummary } = useQuery({
    queryKey: ["security", "summary"],
    queryFn: security.summary,
    refetchInterval: 60_000,
  });
  // Worst open finding on the project's server: critical or known-exploited
  // = error, high = warning, anything else = no dot.
  const alertByProject: Record<string, "error" | "warning"> = {};
  for (const h of securitySummary?.hosts ?? []) {
    const level =
      h.exploited > 0 || h.open_critical > 0 ? "error" : h.open_high > 0 ? "warning" : null;
    if (level) for (const p of h.projects) alertByProject[p.project_id] = level;
  }

  const selectedId = useUI((s) => s.selectedProjectId);
  const setSelected = useUI((s) => s.setSelectedProjectId);

  const runningByProject: Record<string, number> = {};
  for (const c of board?.columns ?? []) {
    if (c.status === "in_progress" || c.status === "needs_input") {
      for (const t of c.tasks) {
        runningByProject[t.project_id] = (runningByProject[t.project_id] ?? 0) + 1;
      }
    }
  }

  // Local optimistic copy so the drag preview reads from React state rather
  // than React Query's frozen reference. Re-syncs whenever the server list
  // settles, so an external sync_docs / archive still flows through.
  const [order, setOrder] = useState<Project[]>([]);
  useEffect(() => {
    if (list) setOrder(list);
  }, [list]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const reorder = useMutation({
    mutationFn: (ids: string[]) => projects.reorder(ids),
    onSuccess: (next) => {
      setOrder(next);
      qc.setQueryData(
        ["projects", { includeInactive: !!showArchived }],
        next.filter((p) => (showArchived ? true : p.active)),
      );
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = order.findIndex((p) => p.id === active.id);
    const newIndex = order.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(order, oldIndex, newIndex);
    setOrder(next);
    reorder.mutate(next.map((p) => p.id));
  };

  const sortableIds = useMemo(() => order.map((p) => p.id), [order]);

  if (isLoading) {
    return (
      <div className="px-3 py-2 space-y-2">
        <Skeleton height={28} />
        <Skeleton height={28} />
        <Skeleton height={28} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-3 py-3 text-small text-status-error">
        Failed to load projects.
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <AllProjectsRow
        collapsed={collapsed}
        active={selectedId === null}
        onClick={() => setSelected(null)}
        totalRunning={Object.values(runningByProject).reduce((a, b) => a + b, 0)}
      />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          {order.map((p) => (
            <ProjectRow
              key={p.id}
              project={p}
              collapsed={collapsed}
              active={p.id === selectedId}
              running={runningByProject[p.id] ?? 0}
              alert={alertByProject[p.id] ?? null}
              onClick={() => setSelected(p.id)}
              onEdit={() => onEdit(p)}
            />
          ))}
        </SortableContext>
      </DndContext>
      {!collapsed && (
        <button
          onClick={onNew}
          className={cn(
            "mx-3 mt-3 mb-2 h-9 px-3 inline-flex items-center gap-2",
            "text-small text-text-tertiary",
            "rounded-md border border-dashed border-border-default",
            "hover:text-text-primary hover:border-border-strong hover:bg-bg-hover",
            "transition-colors duration-fast ease-out",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-panel",
          )}
        >
          <Plus />
          <span>New project</span>
        </button>
      )}
    </div>
  );
}

/** Open security findings on the host: red for critical / exploited, amber for high. */
function SecurityDot({
  level,
  className,
}: {
  level: "error" | "warning";
  className?: string;
}) {
  const label =
    level === "error"
      ? "Critical or actively exploited vulnerability on this host"
      : "High-severity vulnerability on this host";
  return (
    <span
      role="status"
      aria-label={label}
      title={label}
      className={cn("inline-block h-2 w-2 rounded-full shrink-0", className)}
      style={{ background: `var(--status-${level})` }}
    />
  );
}

function AllProjectsRow({
  collapsed,
  active,
  onClick,
  totalRunning,
}: {
  collapsed?: boolean;
  active: boolean;
  onClick: () => void;
  totalRunning: number;
}) {
  return (
    <button
      onClick={onClick}
      aria-current={active}
      className={cn(
        "group relative w-full text-left flex items-center gap-3 px-3 h-9",
        "transition-colors duration-fast ease-out",
        active
          ? "bg-accent-soft text-text-primary"
          : "text-text-secondary hover:bg-bg-hover hover:text-text-primary",
      )}
    >
      {active && (
        <span
          className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r"
          style={{ background: "var(--accent)" }}
          aria-hidden="true"
        />
      )}
      <Tile mode="all" />
      {!collapsed && (
        <>
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-medium text-text-primary truncate">
              All projects
            </div>
          </div>
          {totalRunning > 0 && <RunningBadge count={totalRunning} />}
        </>
      )}
    </button>
  );
}

function ProjectRow({
  project,
  collapsed,
  active,
  running,
  alert,
  onClick,
  onEdit,
}: {
  project: Project;
  collapsed?: boolean;
  active: boolean;
  running: number;
  alert: "error" | "warning" | null;
  onClick: () => void;
  onEdit: () => void;
}) {
  const qc = useQueryClient();
  const isSelf =
    project.name.toLowerCase() === "maestro" ||
    project.name.toLowerCase().includes("self");
  const tint = TILE_BACKGROUNDS[hashId(project.id, TILE_BACKGROUNDS.length)];
  const archived = !project.active;

  const [confirmHard, setConfirmHard] = useState(false);
  const [actionResult, setActionResult] = useState<
    | { kind: "test-ssh" | "sync-docs"; status: "pending" | "ok" | "error"; message: string }
    | null
  >(null);

  const sortable = useSortable({ id: project.id });
  const dragStyle = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    zIndex: sortable.isDragging ? 5 : undefined,
  };

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["projects"] });

  const archive = useMutation({
    mutationFn: () => projects.deactivate(project.id),
    onSuccess: invalidate,
  });
  const restore = useMutation({
    mutationFn: () => projects.restore(project.id),
    onSuccess: invalidate,
  });
  const hardDelete = useMutation({
    mutationFn: () => projects.hardDelete(project.id),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["kanban"] });
      setConfirmHard(false);
    },
  });
  const syncDocs = useMutation({
    mutationFn: () => projects.syncDocs(project.id),
    onSuccess: (data) => {
      const synced = Object.entries(data.results)
        .filter(([, ok]) => ok)
        .map(([k]) => k);
      const missed = Object.entries(data.results)
        .filter(([, ok]) => !ok)
        .map(([k]) => k);
      setActionResult({
        kind: "sync-docs",
        status: missed.length === Object.keys(data.results).length ? "error" : "ok",
        message:
          (synced.length ? `Pulled: ${synced.join(", ")}.` : "") +
          (missed.length ? `\nNot found on worker: ${missed.join(", ")}.` : ""),
      });
    },
    onError: (e) =>
      setActionResult({
        kind: "sync-docs",
        status: "error",
        message: e instanceof Error ? e.message : String(e),
      }),
  });
  const testSsh = useMutation({
    mutationFn: () => projects.testSsh(project.id),
    onSuccess: (r) =>
      setActionResult({
        kind: "test-ssh",
        status: r.ok ? "ok" : "error",
        message: r.message,
      }),
    onError: (e) =>
      setActionResult({
        kind: "test-ssh",
        status: "error",
        message: e instanceof Error ? e.message : String(e),
      }),
  });

  const runSyncDocs = () => {
    setActionResult({ kind: "sync-docs", status: "pending", message: "Pulling docs…" });
    syncDocs.mutate();
  };
  const runTestSsh = () => {
    setActionResult({ kind: "test-ssh", status: "pending", message: "Connecting…" });
    testSsh.mutate();
  };

  // One list of actions, rendered twice: the "…" dropdown and the right-click
  // context menu share it so they can never drift apart.
  const actions: {
    label: string;
    onSelect: () => void;
    variant?: "destructive";
    separatorBefore?: boolean;
  }[] = [
    { label: "Edit", onSelect: onEdit },
    { label: "Sync docs", onSelect: runSyncDocs },
    { label: "Test SSH", onSelect: runTestSsh },
    archived
      ? { label: "Restore", onSelect: () => restore.mutate(), separatorBefore: true }
      : { label: "Archive", onSelect: () => archive.mutate(), separatorBefore: true },
    {
      label: "Delete permanently",
      onSelect: () => setConfirmHard(true),
      variant: "destructive",
    },
  ];

  const menuItems = actions.map((a) => (
    <Fragment key={a.label}>
      {a.separatorBefore && <MenuSeparator />}
      <MenuItem variant={a.variant} onSelect={a.onSelect}>
        {a.label}
      </MenuItem>
    </Fragment>
  ));

  const ctxItems = actions.map((a) => (
    <Fragment key={a.label}>
      {a.separatorBefore && <CtxMenuSeparator />}
      <CtxMenuItem variant={a.variant} onSelect={a.onSelect}>
        {a.label}
      </CtxMenuItem>
    </Fragment>
  ));

  return (
    <>
      <ContextMenu items={ctxItems}>
        <div
          ref={sortable.setNodeRef}
          style={dragStyle}
          {...sortable.attributes}
          {...sortable.listeners}
          onClick={onClick}
          role="button"
          tabIndex={0}
          aria-current={active}
          title={collapsed ? project.name : undefined}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick();
            }
          }}
          className={cn(
            "group relative w-full text-left flex items-center gap-3 px-3 h-9 cursor-pointer",
            "transition-colors duration-fast ease-out",
            active
              ? "bg-accent-soft text-text-primary"
              : "text-text-secondary hover:bg-bg-hover hover:text-text-primary",
            archived && "opacity-55",
            sortable.isDragging && "bg-bg-surface shadow-sm",
          )}
        >
          {active && (
            <span
              className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r"
              style={{ background: "var(--accent)" }}
              aria-hidden="true"
            />
          )}
          <Tile
            mode="project"
            tint={tint}
            initial={project.name.slice(0, 1).toUpperCase()}
            showSettings={isSelf}
          />
          {collapsed && alert && (
            <SecurityDot level={alert} className="absolute top-1.5 right-1.5" />
          )}
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-medium text-text-primary truncate">
                  {project.name}
                  {archived && (
                    <span className="ml-2 text-mono text-text-quaternary">
                      archived
                    </span>
                  )}
                </div>
              </div>
              {alert && <SecurityDot level={alert} />}
              {running > 0 && <RunningBadge count={running} />}
              <div
                className={cn(
                  "shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100",
                  "transition-opacity duration-fast ease-out",
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <Menu
                  trigger={
                    <IconButton
                      icon={<MoreHorizontal />}
                      label={`Project menu — ${project.name}`}
                      size="sm"
                    />
                  }
                >
                  {menuItems}
                </Menu>
              </div>
            </>
          )}
        </div>
      </ContextMenu>
      <TypedConfirmDialog
        open={confirmHard}
        onClose={() => setConfirmHard(false)}
        onConfirm={() => hardDelete.mutate()}
        title={`Delete "${project.name}" permanently?`}
        description="This removes the project, all of its tasks (including archived), cost events, and synced docs. This cannot be undone."
        requireText={project.name}
        confirmLabel="Delete permanently"
        pending={hardDelete.isPending}
      />
      {actionResult && (
        <Modal
          open
          onClose={() => setActionResult(null)}
          title={
            actionResult.kind === "test-ssh" ? "SSH connection test" : "Sync docs"
          }
          description={`Project: ${project.name}`}
          footer={
            <>
              <div className="flex-1" />
              <Button variant="primary" onClick={() => setActionResult(null)}>
                Close
              </Button>
            </>
          }
        >
          <ResultBody result={actionResult} />
        </Modal>
      )}
    </>
  );
}

function ResultBody({
  result,
}: {
  result: { status: "pending" | "ok" | "error"; message: string };
}) {
  const dotColor =
    result.status === "ok"
      ? "var(--status-success)"
      : result.status === "error"
        ? "var(--status-error)"
        : "var(--status-running)";
  const label =
    result.status === "ok"
      ? "Connected"
      : result.status === "error"
        ? "Failed"
        : "In progress…";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "inline-block h-2 w-2 rounded-full",
            result.status === "pending" && "motion-safe:animate-pulse",
          )}
          style={{ background: dotColor }}
          aria-hidden="true"
        />
        <span className="text-small text-text-secondary">{label}</span>
      </div>
      <pre
        className={cn(
          "text-mono text-text-secondary whitespace-pre-wrap break-words",
          "bg-bg-inset border border-border-subtle rounded-md p-3 max-h-[40vh] overflow-auto",
        )}
      >
        {result.message || "—"}
      </pre>
    </div>
  );
}

function Tile({
  mode,
  tint,
  initial,
  showSettings,
}: {
  mode: "all" | "project";
  tint?: string;
  initial?: string;
  showSettings?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-6 w-6 items-center justify-center rounded-sm",
        "text-[11px] font-medium text-text-secondary shrink-0",
      )}
      style={{ background: tint ?? "var(--bg-inset)" }}
      aria-hidden="true"
    >
      {mode === "all" ? <Inbox /> : showSettings ? <Settings /> : (initial ?? <Folder />)}
    </span>
  );
}

function RunningBadge({ count }: { count: number }) {
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1",
        "rounded-full text-[11px] font-mono",
        "bg-status-running-soft text-status-running",
      )}
      title={`${count} live`}
    >
      {count}
    </span>
  );
}

export type { Task };
